// CDC-TMA — Kanban user feedback bus.
//
// Reuses the existing kanban CustomEvent pattern (see KanbanBoard's
// `kanbanTransitionRequest` / `kanbanArchiveTask` / `kanbanCardUpdated`
// listeners) instead of inventing a new mechanism: an imperative handler
// dispatches `kanbanNotify` on `document`, and <KanbanToast> (mounted by
// KanbanBoard) listens and renders an accessible toast. This replaces the
// blocking, inaccessible `alert()` calls used on refusals (403, 422, …).

/**
 * Known refusal/feedback reasons. Each drives an icon + label in the toast so
 * the meaning is never carried by colour alone (a11y).
 * @typedef {'workflow'|'permission'|'subtasks'|'error'} KanbanNotifyReason
 */

/**
 * Notify the user that an action was refused (or failed).
 *
 * @param {Object}              params
 * @param {KanbanNotifyReason}  params.reason   why the action was refused.
 * @param {string}              params.message  human-readable message (FR).
 */
export function notifyRefusal({ reason, message }) {
  if (typeof document === 'undefined') return;
  const detail = {
    reason: reason || 'error',
    message: message || 'Une erreur est survenue.',
  };
  document.dispatchEvent(new CustomEvent('kanbanNotify', { detail }));
}

/**
 * Classify a server refusal message (FR, from the backend) into a {reason,
 * message} the toast can render. Shared by the board (drop / transition menu)
 * and the task sheet (status switch save) so both stay consistent.
 *
 * The server text may be wrapped as "Task save failed (NNN): <message>" or be a
 * bare DataSync status message; we keep the human text and only DERIVE the icon/
 * label. Most specific motif first; 'workflow' never matches the over-broad bare
 * 'état/state' token (it co-occurs in the permission message).
 *
 * @param {string} rawMessage  the raw error/status message.
 * @returns {{reason: KanbanNotifyReason, message: string}}
 */
export function classifyRefusal(rawMessage) {
  const serverMsg = String(rawMessage || '')
    .replace(/^Task save failed \(\d+\):\s*/, '')
    .trim();
  const lower = serverMsg.toLowerCase();
  let reason = 'error';
  if (/sous-tâche|sous-tache|subtask|enfant|child/.test(lower)) {
    reason = 'subtasks';
  } else if (/403|permission|droit|autoris|rôle|role/.test(lower)) {
    reason = 'permission';
  } else if (/transition|workflow/.test(lower)) {
    reason = 'workflow';
  }
  const fallback = {
    permission: "Action impossible : votre rôle sur ce projet ne permet pas cette transition.",
    subtasks: "Action impossible : des sous-tâches encore ouvertes doivent être terminées d'abord.",
    workflow: "Action impossible : transition non prévue par le workflow de ce type de tâche.",
    error: "Le changement n'a pas pu être enregistré. Veuillez réessayer.",
  };
  return { reason, message: serverMsg || fallback[reason] };
}
